import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { useCreateSetupIntent, usePaymentMethods, useSetDefaultPaymentMethod } from "../api/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../api/hooks";
import { toast } from "react-toastify";
import { ProfileResponse } from "../api/ApiClient";

interface AddCardModalProps {
  show: boolean;
  onHide: () => void;
  profile?: ProfileResponse | null;
}

const AddCardModal: React.FC<AddCardModalProps> = ({ show, onHide, profile }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripe, setStripe] = useState<any>(null);
  const [elements, setElements] = useState<any>(null);
  const [cardElement, setCardElement] = useState<any>(null);
  const [shouldSetAsDefault, setShouldSetAsDefault] = useState(false);
  const createSetupIntentMutation = useCreateSetupIntent();
  const setDefaultMutation = useSetDefaultPaymentMethod();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (show && !stripe) {
      // Load Stripe.js dynamically
      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/";
      script.async = true;
      script.onload = () => {
        initializeStripe();
      };
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [show]);

  const initializeStripe = async () => {
    try {
      const response = await createSetupIntentMutation.mutateAsync(undefined);
      const { publishableKey, setupIntent, shouldSetAsDefault: shouldSetDefault } = response.data;

      // Store whether we should set this card as default after adding
      setShouldSetAsDefault(shouldSetDefault || false);

      if (!(window as any).Stripe) {
        console.error("Stripe.js not loaded");
        return;
      }

      const stripeInstance = (window as any).Stripe(publishableKey);
      setStripe(stripeInstance);

      // Prepare default values from profile
      const defaultValues: any = {};
      if (profile) {
        defaultValues.billingDetails = {
          name: `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || undefined,
          email: profile.email || undefined,
          phone: profile.mobile || undefined,
        };

        // Add address if available
        if (profile.streetNumber || profile.streetName || profile.suburb || profile.state || profile.postcode) {
          defaultValues.billingDetails.address = {
            line1: `${profile.streetNumber || ''} ${profile.streetName || ''} ${profile.streetType || ''}`.trim() || undefined,
            city: profile.suburb || undefined,
            state: profile.state || undefined,
            postal_code: profile.postcode || undefined,
            country: 'AU', // Assuming Australia
          };
        }
      }

      const elementsInstance = stripeInstance.elements({
        clientSecret: setupIntent,
      });
      setElements(elementsInstance);

      const cardElementInstance = elementsInstance.create("payment", {
        layout: {
          type: "tabs",
          defaultCollapsed: false,
        },
        defaultValues: Object.keys(defaultValues).length > 0 ? defaultValues : undefined,
      });
      cardElementInstance.mount("#card-element");
      setCardElement(cardElementInstance);
    } catch (error) {
      console.error("Error initializing Stripe:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin + "/details/payment",
        },
        redirect: "if_required",
      });

      if (error) {
        toast.error(error.message || "Failed to add payment method");
      } else {
        // If this is the first card, set it as default
        if (shouldSetAsDefault && setupIntent?.payment_method) {
          try {
            await setDefaultMutation.mutateAsync(setupIntent.payment_method);
            toast.success("Payment method added and set as default");
          } catch (err) {
            // Card was added but failed to set as default
            console.error("Failed to set as default:", err);
            toast.success("Payment method added successfully");
          }
        } else {
          toast.success("Payment method added successfully");
        }
        
        queryClient.invalidateQueries({ queryKey: queryKeys.paymentMethods });
        onHide();
      }
    } catch (err: any) {
      toast.error(err.message || "An error occurred");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (cardElement) {
      cardElement.unmount();
    }
    setStripe(null);
    setElements(null);
    setCardElement(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Payment Method</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} id="payment-form">
          <div id="card-element" style={{ minHeight: "200px" }}></div>
          {createSetupIntentMutation.isPending && (
            <div className="text-center mt-3">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="ms-2">Initializing...</span>
            </div>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isProcessing || !stripe || !elements}
          style={{ backgroundColor: "#ff6600", borderColor: "#ff6600" }}
        >
          {isProcessing ? "Processing..." : "Add Card"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddCardModal;
